/**
 * Registro dei trattamenti (GDPR Art. 30(1)) shown as the register itself: numbered rows,
 * one column per letter of the article, printable and exportable. Templates are hidden.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useList } from "@refinedev/core";
import { useOrgScope } from "@evo-flywheel/ui";
import { Button, Card, Space, Table, Tag, Typography } from "antd";
import { DownloadOutlined, PlusOutlined, PrinterOutlined } from "@ant-design/icons";
import { TONE_FRAME_CLASS } from "@evo-flywheel/ui";

const COLUMNS: Array<{ field: string; title: string; letter: string }> = [
  { field: "processingname", title: "Trattamento", letter: "" },
  { field: "purpose_of_processing", title: "Finalità", letter: "b" },
  { field: "legal_basis", title: "Base giuridica", letter: "b" },
  { field: "categories_of_data_subjects", title: "Interessati", letter: "c" },
  { field: "categories_of_personal_data", title: "Dati personali", letter: "c" },
  { field: "categories_of_recipients", title: "Destinatari", letter: "d" },
  { field: "transfers_to_third_countries", title: "Trasferimenti extra-UE", letter: "e" },
  { field: "erasure_time_limits", title: "Conservazione", letter: "f" },
  { field: "security_measures", title: "Misure di sicurezza", letter: "g" },
];
const BASIS_COLOR: Record<string, string> = { consenso: "blue", contratto: "green", "obbligo legale": "purple", "interesse legittimo": "gold", "interesse vitale": "red", "interesse pubblico": "cyan" };
const basisColor = (b: string) => Object.entries(BASIS_COLOR).find(([k]) => b.toLowerCase().includes(k))?.[1] ?? "default";

const csvCell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export const ProcessingRegister: React.FC = () => {
  const navigate = useNavigate();
  const scope = useOrgScope("cmp_processing_activity");
  const { data, isLoading } = useList({ resource: "cmp_processing_activity", filters: scope.filters, pagination: { pageSize: 1000 } });
  const rows = ((data?.data ?? []) as Array<Record<string, unknown>>)
    .filter((r) => !(r.is_template === true || r.is_template === "1"))
    .sort((a, b) => String(a.processingname ?? "").localeCompare(String(b.processingname ?? "")));

  const exportCsv = () => {
    const head = ["N.", ...COLUMNS.map((c) => c.title)].map(csvCell).join(";");
    const body = rows.map((r, i) => [i + 1, ...COLUMNS.map((c) => r[c.field])].map(csvCell).join(";"));
    const blob = new Blob(["﻿" + [head, ...body].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `registro-trattamenti-${new Date().toISOString().slice(0, 10)}.csv` });
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return <Card className={`${TONE_FRAME_CLASS} cmp-register`} loading={isLoading} title="Registro dei trattamenti"
    extra={<Space className="cmp-no-print">
      <Button icon={<PrinterOutlined />} onClick={() => window.print()}>Stampa</Button>
      <Button icon={<DownloadOutlined />} onClick={exportCsv}>Esporta CSV</Button>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/cmp_processing_activity/create")}>Nuovo</Button>
    </Space>}>
    <Typography.Title level={4} style={{ marginTop: 0 }}>Registro delle attività di trattamento</Typography.Title>
    <Typography.Paragraph type="secondary">ai sensi dell'Art. 30(1) del Regolamento (UE) 2016/679 — {rows.length} trattamenti</Typography.Paragraph>
    <Table size="small" bordered rowKey={(r) => String(r.id)} dataSource={rows} pagination={false} scroll={{ x: 1400 }}
      onRow={(r) => ({ onClick: () => navigate(`/cmp_processing_activity/show/${encodeURIComponent(String(r.id))}`), style: { cursor: "pointer" } })}
      columns={[
        { title: "N.", width: 48, render: (_, __, i) => i + 1 },
        ...COLUMNS.map((c) => ({
          title: c.letter ? <span>{c.title} <Typography.Text type="secondary">({c.letter})</Typography.Text></span> : c.title,
          dataIndex: c.field,
          width: c.field === "processingname" ? 220 : undefined,
          render: (v: unknown) => !v ? <Typography.Text type="secondary">—</Typography.Text>
            : c.field === "legal_basis" ? <Space size={2} wrap>{String(v).split(/\s*[,;|]+\s*|\s*\|##\|\s*/).filter(Boolean).map((b) => <Tag key={b} color={basisColor(b)}>{b}</Tag>)}</Space>
              : c.field === "processingname" ? <Typography.Text strong>{String(v)}</Typography.Text> : String(v),
        })),
      ]} />
    <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
      Lettere dell'Art. 30(1): (b) finalità, (c) categorie di interessati e di dati, (d) destinatari, (e) trasferimenti verso
      paesi terzi, (f) termini di cancellazione, (g) misure di sicurezza. Il titolare (a) è l'organizzazione del profilo.
    </Typography.Paragraph>
  </Card>;
};
